import AuthLayout from "./AuthLayout";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import OTPInput from "react-otp-input";
import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useVerifyOtpMutation, useSendOtpMutation } from "../../store/api/authAPI";
import { setToken, setUser } from "../../store/slices/authSlice";
import { showToast, showError } from "../../constants/toasts";

const OtpVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [otp, setOtp] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [resendTimer, setResendTimer] = useState<number>(30);
  const [canResend, setCanResend] = useState<boolean>(false);

  const [verifyOtpMutation, { isLoading: isVerifying }] = useVerifyOtpMutation();
  const [sendOtpMutation, { isLoading: isResending }] = useSendOtpMutation();

  // Get email from navigation state
  useEffect(() => {
    const stateEmail = (location.state as any)?.email;
    if (stateEmail) {
      setEmail(stateEmail);
    } else {
      // If no email in state, redirect back to forgot password
      showError("Email not found. Please try again.");
      navigate("/forgot-password");
    }
  }, [location, navigate]);

  // Resend OTP timer
  useEffect(() => {
    if (resendTimer > 0 && !canResend) {
      const timer = setTimeout(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (resendTimer === 0) {
      setCanResend(true);
    }
  }, [resendTimer, canResend]);

  // Validate OTP
  const validateOtp = (): boolean => {
    if (!otp || otp.length !== 4) {
      showError("Please enter a valid 4-digit OTP");
      return false;
    }
    if (!/^\d+$/.test(otp)) {
      showError("OTP must contain only numbers");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateOtp()) {
      return;
    }

    if (!email) {
      showError("Email is required. Please try again.");
      navigate("/forgot-password");
      return;
    }

    try {
      const response = await verifyOtpMutation({
        otpType: 3,
        email: email,
        otp: otp,
      }).unwrap();

      if (response) {
      
        // Extract token from nested response structure: response.data.data.token
        const token = response?.data?.token;
       
        const message = response?.data?.message || response?.message || "OTP verified successfully!";
        
        // Save token if present in response (for persistence)
        if (token) {
          dispatch(setToken(token));
          // Save user data if present
          if (response?.data?.data?.user || response?.user) {
            dispatch(setUser(response?.data?.data?.user || response?.user));
          }
        }
        
        showToast(message);
        // Navigate to reset password page with email
        navigate("/reset-password", { state: { email: email } });
      }
    } catch (error: any) {
      // Handle error response
      let errorMessage = "Invalid OTP. Please try again.";

      // Handle PARSING_ERROR (when server returns HTML instead of JSON)
      if (
        error?.status === "PARSING_ERROR" ||
        error?.error?.status === "PARSING_ERROR"
      ) {
        const originalStatus =
          error?.originalStatus || error?.error?.originalStatus;
        if (originalStatus === 404) {
          errorMessage = "Endpoint not found. Please check the API configuration.";
        } else {
          errorMessage =
            "Server returned an invalid response. Please check the API endpoint.";
        }
        console.error("PARSING_ERROR details:", error);
      } else {
        // Try to extract error message from various error formats
        errorMessage =
          error?.data?.message ||
          error?.data?.error ||
          error?.error?.data?.message ||
          error?.error?.data?.error ||
          error?.message ||
          errorMessage;
      }

      showError(errorMessage);
      // Clear OTP when verification fails
      setOtp("");
    }
  };

  const handleResendOtp = async () => {
    if (!canResend || isResending) {
      return;
    }

    if (!email) {
      showError("Email is required. Please try again.");
      navigate("/forgot-password");
      return;
    }

    // Clear OTP immediately when resend is clicked
    setOtp("");

    try {
      const response = await sendOtpMutation({
        email: email,
        otpType: 3,
      }).unwrap();

      if (response) {
        showToast(response.message || "OTP has been resent to your email address");
        setResendTimer(30);
        setCanResend(false);
      }
    } catch (error: any) {
      // Handle error response
      let errorMessage = "Failed to resend OTP. Please try again later.";

      // Handle PARSING_ERROR
      if (
        error?.status === "PARSING_ERROR" ||
        error?.error?.status === "PARSING_ERROR"
      ) {
        const originalStatus =
          error?.originalStatus || error?.error?.originalStatus;
        if (originalStatus === 404) {
          errorMessage = "Endpoint not found. Please check the API configuration.";
        } else {
          errorMessage =
            "Server returned an invalid response. Please check the API endpoint.";
        }
        console.error("PARSING_ERROR details:", error);
      } else {
        // Try to extract error message from various error formats
        errorMessage =
          error?.data?.message ||
          error?.data?.error ||
          error?.error?.data?.message ||
          error?.error?.data?.error ||
          error?.message ||
          errorMessage;
      }

      showError(errorMessage);
    }
  };

  return (
    <AuthLayout>
      <div className="authBox">
        <h2>Verify Email Address</h2>
        <p>
          A verification OTP has been sent to your email. Please check your
          inbox.
        </p>
        <form className="form" onSubmit={handleSubmit}>
          <div className="control_group">
            <label htmlFor="otp">Enter OTP</label>
            <div className="opt_fields">
              <OTPInput
                value={otp}
                onChange={setOtp}
                numInputs={4}
                renderInput={(props) => <input {...props} id="otp" />}
                inputType="tel"
                shouldAutoFocus
              />
            </div>
            {email && (
              <p style={{ marginTop: "10px", fontSize: "14px", color: "#666" }}>
                OTP sent to: {email}
              </p>
            )}
            <div className="auth_bottom">
              <p>
                <button
                  type="button"
                  className="link"
                  onClick={handleResendOtp}
                  disabled={!canResend || isResending}
                >
                  {isResending
                    ? "Resending..."
                    : canResend
                    ? "Resend OTP"
                    : `Resend OTP (${resendTimer}s)`}
                </button>
              </p>
            </div>
          </div>
          <div className="form_btn">
            <Button
              type="submit"
              className="btn btn_primary w_100"
              variant="contained"
              disabled={isVerifying || otp.length !== 4}
            >
              {isVerifying ? "Verifying..." : "Submit"}
            </Button>
          </div>
        </form>
        <div className="auth_bottom">
          <p>
            <button
              type="button"
              className="link"
              onClick={() => navigate("/")}
              disabled={isVerifying || isResending}
            >
              <KeyboardBackspaceIcon /> Back to Login
            </button>
          </p>
        </div>
      </div>
    </AuthLayout>
  );
};

export default OtpVerification;
